import _ from "lodash";
import { computed } from "mobx";
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from "react-router-dom";
import { usePipedriveStore } from '../..';
import useCircleWithRadius from '../../hooks/useCircleWithRadius';
import useCircleWithRadiusOnMap from "../../hooks/useCircleWithRadiusOnMap";
import useDealsWithFocusArea from "../../hooks/useDealsWithFocusArea";
import useDealsWithGlobalFilters from "../../hooks/useDealsWithGlobalFilters";
import { IDetailsTabProps } from "../../interfaces/IDetailsTabProps";
import { calculateVariance, formatNumber } from '../../utils/utils';
import RadiusDropdown from "../RadiusDropdown";
import DealsGrid from "./DealsGrid";

function DealDetailsDealsTab({ visible, setEntityCount }: IDetailsTabProps) {
    const { id } = useParams();
    const pipedriveStore = usePipedriveStore();
    const currentDeal = useMemo(() => computed(() => pipedriveStore.deals.find(d => d.id.toString() === id)!), [id]).get();
    const coordinates = useMemo(() => ({ latitude: currentDeal.lat, longitude: currentDeal.lon }), [currentDeal]);
    const [radius, setRadius] = useState(5);
    const circle = useCircleWithRadius(coordinates, radius)!;
    useCircleWithRadiusOnMap(coordinates, radius, visible, "DealDetailsDealsTab");
    
    const dealsInRadius = useMemo(() => computed(() => pipedriveStore.deals.filter(d => 
        d !== currentDeal && circle.isPointInside({ latitude: d.lat, longitude: d.lon })
    )), [circle, currentDeal]).get();
    
    const deals = useDealsWithFocusArea(useDealsWithGlobalFilters(dealsInRadius));

    const dealsWithVariance = useMemo(() => deals.map(item => ({
        ...item,
        variance: calculateVariance(
            item.landSizeAcres,
            item.buildingSize,
            currentDeal.landSizeAcres,
            currentDeal.buildingSize,
        ),
    })), [deals, currentDeal]);

    const sortedDeals = useMemo(() => _.orderBy(dealsWithVariance, d => d.variance, "asc"), [dealsWithVariance]);

    useEffect(() => setEntityCount(deals.length), [deals]);

    if (!visible)
        return null;

    return (
        <DealsGrid
            deals={sortedDeals}
            additionalHeadersBefore={[{
                align: "left",
                titleStrings: ["VARIANCE"],
                getValue: item => item.variance,
                getLabel: item => item.variance !== null ? formatNumber(item.variance) : "",
            }]}
            additionalHeadersAfter={[{
                titleStrings: ["DISTANCE"],
                getValue: item => circle!.getDistanceFromCenter({ latitude: item.lat, longitude: item.lon }),
                getLabel: item => {
                    const distance = circle!.getDistanceFromCenter({ latitude: item.lat, longitude: item.lon });
                    return distance ? formatNumber(distance) : "";
                }
            }]}
            radiusDropdown={<RadiusDropdown value={radius} onChange={setRadius} />}
        />
    );
}

export default observer(DealDetailsDealsTab);
